/**
 * lib/render-policy/display-comparables.ts — side-by-side comparison resolver
 * (PIP lean rebuild, WS6). PURE functions over pre-fetched rows: the PDP route
 * reads fin_comparables (scored [{fin, score, basis}] from
 * pip-compute-comparables) plus the resolved canonical/product info for those
 * FINs, and this module picks the render set + the spec rows worth comparing.
 *
 * FALLBACK CONTRACT: no comparables / nothing resolves to a live PDP → empty
 * array (the §32 Inline Mini-Comparison keeps its legacy internal-links
 * fallback and self-hides at zero — Real-or-Hidden Rule 3).
 */
import { displayLabelForSpecKey } from './spec-key-map';
import { isSpecKeyHidden } from '../spec-display-policy';

export interface ComparableEntry {
  fin: string;
  score: number;
  basis?: Record<string, unknown> | null;
}

/** Resolved display info for one comparable FIN (fetched by the route). */
export interface CompareTargetData {
  fin: string;
  slug: string;
  brand: string | null;
  manufacturer_model: string | null;
  display_name: string | null;
  finish: string | null;
  type: string | null;
  specs_json: Record<string, unknown> | null;
  features: unknown;
  image: string | null;
  lowest_price_cents: number | null;
  /** P2: the target's live product id + its best affiliate offer id, so the
   * comparison column can carry its own "View Deal" CTA (pos=compare-col-N).
   * Null when no active offer exists — column renders without a CTA. */
  product_id?: string | null;
  offer_id?: string | null;
}

/**
 * Order fin_comparables entries by score, keep the ones that resolved to a
 * live PDP, dedup by FIN, cap at `max`.
 */
export function selectCompareTargets(
  entries: ComparableEntry[] | null | undefined,
  resolved: Map<string, CompareTargetData>,
  max = 3
): CompareTargetData[] {
  const seen = new Set<string>();
  const out: CompareTargetData[] = [];
  const sorted = [...(entries ?? [])].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  for (const e of sorted) {
    if (!e?.fin || seen.has(e.fin)) continue;
    seen.add(e.fin);
    const t = resolved.get(e.fin);
    if (!t?.slug) continue;
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

/** Spec keys worth a comparison row, most-decision-relevant first. */
const PRIORITY_KEYS = [
  'capacity_cu_ft',
  'total_capacity',
  'capacity',
  'width_in',
  'width',
  'height_in',
  'height',
  'depth_in',
  'depth',
  'energy_kwh_yr',
  'annual_energy_use',
  'energy_star',
  'defrost_type',
  'ice_maker',
  'dispenser',
  'door_style',
  'installation_type',
  'noise_db',
  'color',
  'material',
  'voltage',
  'wattage',
  'size',
];

/** Feed-metadata keys that pass the scalar test but are noise in a compare
 * table (canonical specs_json often carries raw feed fields). */
const COMPARE_EXCLUDE = new Set([
  'category',
  'keywords',
  'condition',
  'description',
  'title',
  'brand',
  'model',
  'mpn',
  'sku',
  'gtin',
  'upc',
  'id',
  'url',
  'link',
  'image',
  'images',
]);

/** Scalar, short, and not on the hidden-key denylist → comparable in a table cell. */
function comparableValue(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return v.trim().length > 0 && v.trim().length <= 80;
  return false;
}

/**
 * Pick up to `max` spec keys present (with short scalar values) in at least
 * `minCoverage` of the given spec objects — priority keys first, then by
 * coverage. Keys on the shared hidden-key denylist never surface.
 */
export function pickCompareSpecKeys(
  specsList: Array<Record<string, unknown> | null | undefined>,
  max = 8,
  minCoverage = 2
): string[] {
  const coverage = new Map<string, number>();
  for (const specs of specsList) {
    if (!specs) continue;
    for (const [k, v] of Object.entries(specs)) {
      if (!comparableValue(v) || isSpecKeyHidden(k) || COMPARE_EXCLUDE.has(k.toLowerCase())) continue;
      coverage.set(k, (coverage.get(k) ?? 0) + 1);
    }
  }
  const effectiveMin = Math.min(minCoverage, Math.max(1, specsList.filter(Boolean).length));
  const candidates = [...coverage.entries()].filter(([, n]) => n >= effectiveMin);
  candidates.sort(([ka, na], [kb, nb]) => {
    const pa = PRIORITY_KEYS.indexOf(ka);
    const pb = PRIORITY_KEYS.indexOf(kb);
    const ra = pa === -1 ? PRIORITY_KEYS.length : pa;
    const rb = pb === -1 ? PRIORITY_KEYS.length : pb;
    if (ra !== rb) return ra - rb;
    if (na !== nb) return nb - na;
    return ka.localeCompare(kb);
  });
  return candidates.slice(0, max).map(([k]) => k);
}

/** Human label for a compare row (shared spec-key map handles the fallback). */
export function compareRowLabel(key: string): string {
  return displayLabelForSpecKey(key);
}
